import { Action, ListModel } from "survey-core";
import type { SurveyCreatorModel, Translation } from "survey-creator-core";

const ADD_ALL_LANGUAGES_ITEM_ID = "rubi-add-all-languages";

/**
 * Shows every language as a grid column: adds the ones still offered by the
 * add-language menu and ticks the ones already listed in Language Settings.
 * One `setSelectedLocales` call rebuilds the grid once, where picking each
 * language with `addLocale` would rebuild it twice per language.
 */
export function addAllLanguages(translation: Translation) {
  const unlisted = translation.chooseLanguageActions.flatMap((action) =>
    action.visible !== false && action.id ? [action.id] : [],
  );
  translation.setSelectedLocales([
    ...translation.getVisibleLocales(),
    ...unlisted,
  ]);
  // Private in the typings: hides the added languages from the menu and
  // disables "+" once none are left, as `addLocale` does after each pick.
  translation["updateChooseLanguageActions"]();
}

function addAllLanguagesItem(translation: Translation) {
  // The Language Settings "+" dropdown; private in the survey-creator typings.
  const addLanguageAction: Action | undefined =
    translation["addLanguageAction"];
  const list = addLanguageAction?.data as ListModel | undefined;
  if (!addLanguageAction || !list?.onSelectionChanged) return;

  const addAllItem = new Action({
    id: ADD_ALL_LANGUAGES_ITEM_ID,
    title: "All languages",
  });
  list.setItems([addAllItem, ...translation.chooseLanguageActions], false);

  // The menu hands every pick to `translation.addLocale(item.id)`, which would
  // add our item's id as a locale, so this item is handled here instead.
  const addPickedLanguage = list.onSelectionChanged;
  list.onSelectionChanged = (item, ...params) => {
    if (item.id !== ADD_ALL_LANGUAGES_ITEM_ID) {
      addPickedLanguage(item, ...params);
      return;
    }
    addAllLanguages(translation);
    addLanguageAction.popupModel.hide();
  };
}

/**
 * Puts an "All languages" item at the top of the Translations tab's
 * add-language menu. The tab builds a new Translation model each time it
 * opens, so the item is added on every activation.
 */
export function registerAddAllLanguagesItem(creator: SurveyCreatorModel) {
  creator.onActiveTabChanged.add((_, options) => {
    if (options.tabName !== "translation" || !options.model) return;
    addAllLanguagesItem(options.model as Translation);
  });
}
